import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Avatar } from '../UI';
import { usersApi } from '../../services/api';
import styles from './MentionInput.module.css';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  position?: string;
  department?: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  mode: 'input' | 'textarea';
  rows?: number;
  maxLength?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onSubmit?: () => void; // For Enter key in input mode
  autoFocus?: boolean;
}

// Parse raw value to get display text (for rendering in contentEditable)
function rawToDisplay(raw: string): string {
  return raw
    .replace(/@\[everyone\]/gi, '@Team')
    .replace(/@\[([a-fA-F0-9-]+)\|([^\]]+)\]/g, '@$2');
}

// Get the plain text length (what user sees)
export function getDisplayLength(raw: string): number {
  return rawToDisplay(raw).length;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  mode,
  rows = 3,
  maxLength,
  onKeyDown,
  onSubmit,
  autoFocus,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerPos, setEmojiPickerPos] = useState<{ top: number; left: number } | null>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout>();
  const mentionStartRef = useRef<number>(-1);
  const isComposingRef = useRef(false);
  const lastValueRef = useRef('__uninitialized__');

  // Sync editor content when value changes externally (including initial mount)
  useEffect(() => {
    if (!editorRef.current) return;
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;
    renderContent(value);
  }, [value]);

  // Auto focus
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
    }
  }, [autoFocus]);

  const renderContent = (raw: string) => {
    if (!editorRef.current) return;
    const el = editorRef.current;

    if (!raw) {
      el.innerHTML = '';
      return;
    }

    // First names shared by different mentioned users are ambiguous — show full name
    const firstNameOwners = new Map<string, Set<string>>();
    const scanRegex = /@\[([a-fA-F0-9-]+)\|([^\]]+)\]/g;
    let scan;
    while ((scan = scanRegex.exec(raw)) !== null) {
      const fn = scan[2].trim().split(/\s+/)[0].toLowerCase();
      if (!firstNameOwners.has(fn)) firstNameOwners.set(fn, new Set());
      firstNameOwners.get(fn)!.add(scan[1].toLowerCase());
    }

    // Build HTML with styled mention spans
    const html = raw
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/@\[everyone\]/gi, `<span class="${styles.mentionTag} ${styles.mentionEveryone}" contenteditable="false" data-mention="everyone">@Team</span>`)
      .replace(/@\[([a-fA-F0-9-]+)\|([^\]]+)\]/g, (_m, id, name) => {
        const firstName = name.trim().split(/\s+/)[0];
        const ambiguous = (firstNameOwners.get(firstName.toLowerCase())?.size || 0) > 1;
        return `<span class="${styles.mentionTag}" contenteditable="false" data-mention="${id}" data-name="${name}">@${ambiguous ? name.trim() : firstName}</span>`;
      })
      .replace(/\n/g, '<br>');

    el.innerHTML = html;

    // Place cursor at end
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(el);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  // Extract raw value from contentEditable DOM
  const extractRawValue = useCallback((): string => {
    if (!editorRef.current) return '';
    const parts: string[] = [];

    const walk = (node: Node, isTopLevel: boolean) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parts.push(node.textContent || '');
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName;

        if (tag === 'BR') {
          parts.push('\n');
        } else if (el.dataset.mention) {
          if (el.dataset.mention === 'everyone') {
            parts.push('@[everyone]');
          } else {
            parts.push(`@[${el.dataset.mention}|${el.dataset.name}]`);
          }
        } else if (tag === 'DIV' || tag === 'P') {
          // Chrome wraps lines in <div>, Firefox uses <br>, some use <p>
          // Add newline before block elements (except if it's the first child)
          const lastPart = parts[parts.length - 1];
          if (parts.length > 0 && lastPart !== '\n' && lastPart !== '') {
            parts.push('\n');
          }
          el.childNodes.forEach(child => walk(child, false));
        } else {
          el.childNodes.forEach(child => walk(child, false));
        }
      }
    };

    editorRef.current.childNodes.forEach(child => walk(child, true));
    // Clean up: remove trailing newline
    const result = parts.join('');
    return result.replace(/\n$/, '');
  }, []);

  const handleInput = useCallback(() => {
    if (isComposingRef.current) return;
    const raw = extractRawValue();

    // Max length check
    if (maxLength && getDisplayLength(raw) > maxLength) {
      renderContent(lastValueRef.current);
      return;
    }

    lastValueRef.current = raw;
    onChange(raw);

    // Check for @ trigger
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) {
      setShowDropdown(false);
      return;
    }

    const text = textNode.textContent || '';
    const cursorPos = range.startOffset;

    // Find @ character before cursor
    let atPos = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      if (text[i] === '@') {
        // Valid if at start or preceded by space/newline
        if (i === 0 || /\s/.test(text[i - 1])) {
          atPos = i;
        }
        break;
      }
      if (/\s/.test(text[i])) break;
    }

    if (atPos >= 0) {
      const query = text.slice(atPos + 1, cursorPos);
      setSearchQuery(query);
      mentionStartRef.current = atPos;
      setShowDropdown(true);
      setSelectedIndex(0);

      // Dropdown is rendered inside the wrapper, positioned via CSS

      // Search users
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(async () => {
        setLoading(true);
        try {
          const response = await usersApi.getAll({ search: query, limit: 10, active: true });
          setUsers(response.data.users || response.data || []);
        } catch {
          setUsers([]);
        } finally {
          setLoading(false);
        }
      }, 200);
    } else {
      setShowDropdown(false);
    }
  }, [onChange, extractRawValue, maxLength]);

  const insertMention = useCallback((mentionRaw: string, displayText: string, showFullName?: boolean) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !editorRef.current) return;

    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) return;

    const text = textNode.textContent || '';
    const cursorPos = range.startOffset;
    const atPos = mentionStartRef.current;

    // Replace @query with mention
    const before = text.slice(0, atPos);
    const after = text.slice(cursorPos);

    // Create text node for before, mention span, and after text
    const parent = textNode.parentNode!;
    const fragment = document.createDocumentFragment();

    if (before) fragment.appendChild(document.createTextNode(before));

    const span = document.createElement('span');
    span.className = mentionRaw === '@[everyone]'
      ? `${styles.mentionTag} ${styles.mentionEveryone}`
      : styles.mentionTag;
    span.contentEditable = 'false';
    if (mentionRaw === '@[everyone]') {
      span.dataset.mention = 'everyone';
    } else {
      const match = mentionRaw.match(/@\[([a-fA-F0-9-]+)\|([^\]]+)\]/);
      if (match) {
        span.dataset.mention = match[1];
        span.dataset.name = match[2];
      }
    }
    // Chip shows the first name, or the full name when another user shares it;
    // the full name always stays in data-name
    span.textContent = mentionRaw === '@[everyone]' || showFullName
      ? displayText
      : `@${displayText.replace(/^@/, '').trim().split(/\s+/)[0]}`;
    fragment.appendChild(span);

    // Add space after mention
    const spaceNode = document.createTextNode('\u00A0' + after);
    fragment.appendChild(spaceNode);

    parent.replaceChild(fragment, textNode);

    // Position cursor after the space
    const newRange = document.createRange();
    newRange.setStart(spaceNode, 1);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    setShowDropdown(false);

    // Update value
    const raw = extractRawValue();
    lastValueRef.current = raw;
    onChange(raw);
  }, [onChange, extractRawValue]);

  const handleSelectUser = useCallback((user: User) => {
    const mentionRaw = `@[${user.id}|${user.first_name} ${user.last_name}]`;
    // If another user in the search results shares this first name, keep the
    // full name in the chip so the tag is unambiguous.
    const firstNameClash = users.some(
      u => u.id !== user.id && u.first_name.trim().toLowerCase() === user.first_name.trim().toLowerCase()
    );
    insertMention(mentionRaw, `@${user.first_name} ${user.last_name}`, firstNameClash);
  }, [insertMention, users]);

  const handleSelectEveryone = useCallback(() => {
    insertMention('@[everyone]', '@Team');
  }, [insertMention]);

  const handleKeyDownInternal = useCallback((e: React.KeyboardEvent) => {
    if (showDropdown) {
      const totalItems = 1 + users.length; // everyone + users
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (selectedIndex === 0) {
          handleSelectEveryone();
        } else {
          const user = users[selectedIndex - 1];
          if (user) handleSelectUser(user);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        return;
      }
    }

    // In input mode, Enter submits (unless dropdown is open)
    if (mode === 'input' && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
      return;
    }

    // In textarea mode, prevent Enter from creating newlines when Shift is not held... actually allow newlines
    onKeyDown?.(e);
  }, [showDropdown, users, selectedIndex, mode, onSubmit, onKeyDown, handleSelectEveryone, handleSelectUser]);

  // Insert emoji at cursor
  const insertEmoji = useCallback((emoji: string) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(emoji);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editorRef.current.appendChild(document.createTextNode(emoji));
    }
    const raw = extractRawValue();
    lastValueRef.current = raw;
    onChange(raw);
    setShowEmojiPicker(false);
  }, [extractRawValue, onChange]);

  // Close dropdown and emoji picker on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter dropdown items
  const filteredUsers = searchQuery
    ? users.filter(u =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const showEveryoneOption = !searchQuery || 'team'.includes(searchQuery.toLowerCase()) || 'everyone'.includes(searchQuery.toLowerCase()) || 'all'.includes(searchQuery.toLowerCase());

  const EMOJI_GROUPS = [
    { label: 'Smileys', emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😜', '🤔', '🤗', '😎', '🥳'] },
    { label: 'Gestures', emojis: ['👍', '👎', '👏', '🙌', '🤝', '💪', '✌️', '🤞', '👊', '✋', '🙏', '🫡', '💅', '🫶', '❤️', '🔥', '⭐', '💯', '✅', '🎉'] },
    { label: 'Objects', emojis: ['🏆', '🎯', '💡', '📣', '🚀', '💰', '🎁', '📌', '⚡', '🌟', '☕', '📚', '🎵', '📅', '🔔', '💬', '📝', '🔗', '⏰', '🎊'] },
  ];

  return (
    <div className={`${styles.wrapper} ${className || ''}`}>
      <div
        ref={editorRef}
        className={`${styles.editor} ${mode === 'input' ? styles.editorInput : styles.editorTextarea} ${disabled ? styles.disabled : ''}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDownInternal}
        onCompositionStart={() => { isComposingRef.current = true; }}
        onCompositionEnd={() => { isComposingRef.current = false; handleInput(); }}
        data-placeholder={placeholder}
        style={mode === 'textarea' ? { minHeight: `${rows * 24}px` } : undefined}
        role="textbox"
        aria-multiline={mode === 'textarea'}
      />

      {/* Emoji Picker Button */}
      {!disabled && (
        <div className={styles.emojiWrapper} ref={emojiPickerRef}>
          <button
            type="button"
            className={styles.emojiBtn}
            onMouseDown={(e) => {
              e.preventDefault();
              if (!showEmojiPicker) {
                // Fixed positioning so the picker escapes modal overflow clipping.
                // Prefer opening above the button; flip below if there isn't room.
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const pickerW = window.innerWidth <= 480 ? 250 : 280;
                const pickerH = 260;
                const top = rect.top - pickerH - 8 >= 8
                  ? rect.top - pickerH - 8
                  : Math.min(rect.bottom + 8, window.innerHeight - pickerH - 8);
                const left = Math.max(8, Math.min(rect.right - pickerW, window.innerWidth - pickerW - 8));
                setEmojiPickerPos({ top, left });
              }
              setShowEmojiPicker(!showEmojiPicker);
            }}
            title="Insert emoji"
          >
            😊
          </button>
          {showEmojiPicker && (
            <div
              className={styles.emojiPicker}
              style={emojiPickerPos ? { position: 'fixed', top: emojiPickerPos.top, left: emojiPickerPos.left, right: 'auto', bottom: 'auto' } : undefined}
            >
              {EMOJI_GROUPS.map(group => (
                <div key={group.label} className={styles.emojiGroup}>
                  <div className={styles.emojiGroupLabel}>{group.label}</div>
                  <div className={styles.emojiGrid}>
                    {group.emojis.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        className={styles.emojiItem}
                        onMouseDown={(e) => { e.preventDefault(); insertEmoji(emoji); }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showDropdown && (
        <div className={styles.dropdown}>
          {showEveryoneOption && (
            <div
              className={`${styles.dropdownItem} ${selectedIndex === 0 ? styles.dropdownItemActive : ''}`}
              onMouseDown={(e) => { e.preventDefault(); handleSelectEveryone(); }}
              onMouseEnter={() => setSelectedIndex(0)}
            >
              <div className={styles.everyoneIcon}>@</div>
              <div className={styles.dropdownInfo}>
                <span className={styles.dropdownName}>@Team</span>
                <span className={styles.dropdownMeta}>Notify all users</span>
              </div>
            </div>
          )}
          {loading && <div className={styles.dropdownLoading}>Searching...</div>}
          {!loading && filteredUsers.map((user, idx) => {
            const itemIdx = showEveryoneOption ? idx + 1 : idx;
            return (
              <div
                key={user.id}
                className={`${styles.dropdownItem} ${selectedIndex === itemIdx ? styles.dropdownItemActive : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleSelectUser(user); }}
                onMouseEnter={() => setSelectedIndex(itemIdx)}
              >
                <Avatar
                  src={user.avatar_url}
                  name={`${user.first_name} ${user.last_name}`}
                  size="sm"
                />
                <div className={styles.dropdownInfo}>
                  <span className={styles.dropdownName}>{user.first_name} {user.last_name}</span>
                  <span className={styles.dropdownMeta}>{user.position || user.department || ''}</span>
                </div>
              </div>
            );
          })}
          {!loading && filteredUsers.length === 0 && !showEveryoneOption && (
            <div className={styles.dropdownEmpty}>No users found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
